import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { SectionTitle } from "@/components/common";
import { toast } from "sonner";
import { Trash2, RotateCcw, Loader2, ChevronDown, BellOff, CheckCheck } from "lucide-react";
import { EliminaDefBtn, SvuotaCestinoBtn } from "@/components/CestinoDefinitivo";
import { chiediElimina, esito } from "@/components/SelezioneFile";

const fmt = (iso) => { try { return new Date(iso).toLocaleDateString("it-IT"); } catch { return "—"; } };
const key = (r) => `${r.coll}:${r.id}`;
const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-");

function CestinoRiga({ c, checked, onToggle, onRestore, onDone }) {
  return (
    <div className={`p-3 flex items-center gap-3 flex-wrap ${checked ? "bg-emerald-50" : ""}`} data-testid={`pulizia-cestino-row-${c.id}`}>
      <input type="checkbox" data-testid={`pulizia-cestino-check-${c.id}`} checked={checked} onChange={(e) => onToggle(e.target.checked)} className="w-4 h-4" />
      <div className="flex-1 min-w-0 text-sm break-words">{c.titolo}
        <div className="text-[11px] text-[var(--muted)]" data-testid={`pulizia-cestino-info-${c.id}`}>eliminato il {fmt(c.cestino?.at)} da {c.cestino?.by || "—"} · cancellazione definitiva il {fmt(c.cestino?.scade_il)}</div></div>
      <button data-testid={`pulizia-ripristina-${c.id}`} onClick={() => onRestore([c.id])} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#111111] text-white text-xs font-semibold"><RotateCcw size={14} /> Ripristina</button>
      <EliminaDefBtn ambito="pulizia" id={c.id} onDone={onDone} testid={`pulizia-elimina-def-${c.id}`} />
    </div>
  );
}

function CestinoGruppo({ nome, righe, sel, setSel, onRestore, onDone }) {
  const tutti = righe.every((c) => sel[c.id]);
  const toggleAll = (v) => setSel((s) => { const x = { ...s }; righe.forEach((c) => { x[c.id] = v; }); return x; });
  return (
    <div className="card-immo p-0 mb-3 overflow-hidden" data-testid={`pulizia-cestino-gruppo-${slug(nome)}`}>
      <label className="flex items-center gap-3 p-3 bg-[#F7F7F8] cursor-pointer">
        <input type="checkbox" data-testid={`pulizia-cestino-select-all-${slug(nome)}`} checked={tutti} onChange={(e) => toggleAll(e.target.checked)} className="w-4 h-4" />
        <span className="font-display font-bold text-sm">{nome}</span>
        <span className="text-xs px-2 py-0.5 rounded-full bg-white border border-[var(--border)]" data-testid={`pulizia-cestino-count-${slug(nome)}`}>{righe.length}</span>
      </label>
      <div className="divide-y divide-[var(--border)]">
        {righe.map((c) => <CestinoRiga key={c.id} c={c} checked={!!sel[c.id]} onToggle={(v) => setSel((s) => ({ ...s, [c.id]: v }))} onRestore={onRestore} onDone={onDone} />)}
      </div>
    </div>
  );
}

function Cestino({ list, onRestore, onDone }) {
  const [q, setQ] = useState("");
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  if (!list.length) return null;
  const vis = list.filter((c) => (c.titolo || "").toLowerCase().includes(q.trim().toLowerCase()));
  const gruppi = vis.reduce((g, c) => { (g[c.sezione] = g[c.sezione] || []).push(c); return g; }, {});
  const scelti = vis.filter((c) => sel[c.id]).map((c) => c.id);
  const restore = async (ids) => { setBusy(true); try { await onRestore(ids); setSel({}); } finally { setBusy(false); } };
  const eliminaDef = async (ids) => {
    if (!(await chiediElimina(ids.length))) return;
    setBusy(true);
    try { esito(await Promise.allSettled(ids.map((id) => api.post(`/cestino/pulizia/${id}/elimina-definitivo`, { conferma: "ELIMINA" }))), "eliminati definitivamente"); setSel({}); onDone(); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-8" data-testid="pulizia-cestino">
      <h3 className="font-display font-bold text-sm mb-2">Nel cestino (cancellazione definitiva dopo 30 giorni)</h3>
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <input data-testid="pulizia-cestino-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome…"
          className="flex-1 min-w-[180px] px-3 py-2 rounded-xl border border-[var(--border)] text-sm bg-white" />
        <span className="text-xs text-[var(--muted)]" data-testid="pulizia-cestino-sel-count">{scelti.length} selezionati</span>
        <button data-testid="pulizia-cestino-ripristina-sel-btn" disabled={busy || !scelti.length} onClick={() => restore(scelti)}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#111111] text-white text-sm font-semibold disabled:opacity-40">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <RotateCcw size={15} />} Ripristina selezionati
        </button>
        <button data-testid="pulizia-cestino-elimina-sel-btn" disabled={busy || !scelti.length} onClick={() => eliminaDef(scelti)}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-red-600 text-white text-sm font-semibold disabled:opacity-40">
          <Trash2 size={15} /> Elimina definitivamente selezionati
        </button>
        <SvuotaCestinoBtn ambito="pulizia" count={list.length} onDone={onDone} />
      </div>
      {!vis.length ? <div className="card-immo p-6 text-center text-sm text-[var(--muted)]" data-testid="pulizia-cestino-vuoto">Nessun record trovato.</div>
        : Object.entries(gruppi).map(([nome, righe]) => <CestinoGruppo key={nome} nome={nome} righe={righe} sel={sel} setSel={setSel} onRestore={restore} onDone={onDone} />)}
    </div>
  );
}

function Riga({ r, checked, onToggle }) {
  return (
    <tr data-testid={`pulizia-row-${r.coll}-${r.id}`} className={checked ? "bg-red-50" : ""}>
      <td className="p-3 align-top"><input type="checkbox" data-testid={`pulizia-check-${r.coll}-${r.id}`} checked={checked} onChange={(e) => onToggle(e.target.checked)} className="w-4 h-4" /></td>
      <td className="p-3 align-top break-words font-medium">{r.titolo}
        <div className="sm:hidden text-[11px] font-normal text-[var(--muted)] mt-1">{fmt(r.data)} · {r.creato_da}</div>
        <div className="sm:hidden text-xs font-normal text-[var(--muted)] mt-1">{r.motivo}</div></td>
      <td className="p-3 align-top whitespace-nowrap hidden sm:table-cell">{fmt(r.data)}</td>
      <td className="p-3 align-top hidden sm:table-cell">{r.creato_da}</td>
      <td className="p-3 align-top text-xs text-[var(--muted)] hidden sm:table-cell" data-testid={`pulizia-motivo-${r.coll}-${r.id}`}>{r.motivo}</td>
    </tr>
  );
}

function Gruppo({ nome, righe, sel, setSel }) {
  const [open, setOpen] = useState(false);
  const n = righe.filter((r) => sel[key(r)]).length;
  const tutti = n === righe.length;
  const toggleAll = (v) => setSel((s) => { const x = { ...s }; righe.forEach((r) => { x[key(r)] = v; }); return x; });
  return (
    <div className="card-immo p-0 mb-3 overflow-hidden" data-testid={`pulizia-gruppo-${slug(nome)}`}>
      <div className="flex items-center gap-3 p-3 flex-wrap bg-[#F7F7F8]">
        <label className="inline-flex items-center gap-2 text-xs font-semibold cursor-pointer">
          <input type="checkbox" data-testid={`pulizia-select-all-${slug(nome)}`} checked={tutti} onChange={(e) => toggleAll(e.target.checked)} className="w-4 h-4" />
          Seleziona tutti in questa sezione
        </label>
        <button onClick={() => setOpen((o) => !o)} data-testid={`pulizia-toggle-${slug(nome)}`} className="flex-1 flex items-center gap-2 text-left min-w-[160px]">
          <span className="font-display font-bold text-sm">{nome}</span>
          <span className="text-xs px-2 py-0.5 rounded-full bg-white border border-[var(--border)]" data-testid={`pulizia-count-${slug(nome)}`}>{righe.length}</span>
          {n > 0 && <span className="text-xs text-red-600 font-semibold">{n} selezionati</span>}
          <ChevronDown size={16} className={`ml-auto transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </div>
      {open && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm sm:min-w-[640px]">
            <thead className="text-[10px] uppercase tracking-wider text-[var(--muted)]">
              <tr><th className="p-3 w-10"></th><th className="p-3 text-left">Nome / titolo</th><th className="p-3 text-left hidden sm:table-cell">Data</th><th className="p-3 text-left hidden sm:table-cell">Creato da</th><th className="p-3 text-left hidden sm:table-cell">Perché è sospetto</th></tr>
            </thead>
            <tbody className="divide-y divide-[var(--border)]">
              {righe.map((r) => <Riga key={key(r)} r={r} checked={!!sel[key(r)]} onToggle={(v) => setSel((s) => ({ ...s, [key(r)]: v }))} />)}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AzioniNotifiche({ onDone }) {
  const [busy, setBusy] = useState("");
  const run = async (tipo, url, conferma, msg) => {
    if (!window.confirm(conferma)) return;
    setBusy(tipo);
    try { const { data } = await api.post(url); toast.success(msg(data)); onDone(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const cls = "inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold border border-[var(--border)] bg-white disabled:opacity-40";
  return (
    <div className="flex gap-2 flex-wrap mb-5" data-testid="pulizia-notifiche-azioni">
      <button data-testid="pulizia-elimina-notifiche-prova-btn" disabled={!!busy} className={cls}
        onClick={() => run("del", "/admin/pulizia-dati/notifiche-prova", "Spostare nel cestino tutte le notifiche di prova (collegate a record di prova o a utenti di test)? Recuperabili per 30 giorni.", (d) => `${d.spostati} notifiche spostate nel cestino`)}>
        {busy === "del" ? <Loader2 size={14} className="animate-spin" /> : <BellOff size={14} />} Elimina notifiche di prova
      </button>
      <button data-testid="pulizia-segna-lette-btn" disabled={!!busy} className={cls}
        onClick={() => run("read", "/admin/pulizia-dati/notifiche-lette", "Segnare come lette tutte le notifiche di tutti gli utenti?", (d) => `${d.aggiornate} notifiche segnate come lette`)}>
        {busy === "read" ? <Loader2 size={14} className="animate-spin" /> : <CheckCheck size={14} />} Segna tutte come lette
      </button>
    </div>
  );
}

export default function PuliziaDati() {
  const [rows, setRows] = useState(null);
  const [cest, setCest] = useState([]);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  const load = () => {
    api.get("/admin/pulizia-dati").then((r) => setRows(r.data)).catch((e) => { toast.error(apiError(e)); setRows([]); });
    api.get("/admin/pulizia-dati/cestino").then((r) => setCest(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const scelti = (rows || []).filter((r) => sel[key(r)]);
  const gruppi = (rows || []).reduce((g, r) => { (g[r.sezione] = g[r.sezione] || []).push(r); return g; }, {});
  const elimina = async () => {
    if (!window.confirm(`Sei sicura? ${scelti.length} record verranno spostati nel cestino (recuperabili per 30 giorni).`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/pulizia-dati/elimina", { items: scelti.map((r) => ({ coll: r.coll, id: r.id })) });
      toast.success(`${data.spostati} record spostati nel cestino`);
      if (data.errori?.length) toast.error(`${data.errori.length} non eliminati: ${data.errori[0].errore}`);
      setSel({}); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const ripristina = async (ids) => {
    try {
      const { data } = await api.post("/admin/pulizia-dati/cestino/ripristina", { ids });
      toast.success(`${data.ripristinati} record ripristinati`);
      if (data.errori?.length) toast.error(`${data.errori.length} non ripristinati: ${data.errori[0].errore}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="fade-up" data-testid="pulizia-dati-page">
      <SectionTitle sub="Record che sembrano di prova o dimostrativi, in tutte le sezioni. Niente viene eliminato in automatico: spunta tu le righe da togliere.">Pulizia dati di prova</SectionTitle>
      <AzioniNotifiche onDone={load} />
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <span className="text-sm text-[var(--muted)]" data-testid="pulizia-count">{rows ? `${rows.length} record sospetti · ${scelti.length} selezionati` : "Caricamento…"}</span>
        <button data-testid="pulizia-elimina-btn" onClick={elimina} disabled={busy || !scelti.length}
          className="ml-auto inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-red-600 text-white text-sm font-semibold disabled:opacity-40">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />} Elimina selezionati
        </button>
      </div>
      {!rows ? <div className="skeleton h-40 rounded-2xl" /> : rows.length === 0 ? <div className="card-immo p-10 text-center text-sm text-[var(--muted)]" data-testid="pulizia-vuoto">Nessun record sospetto.</div>
        : Object.entries(gruppi).map(([nome, righe]) => <Gruppo key={nome} nome={nome} righe={righe} sel={sel} setSel={setSel} />)}
      <p className="text-[11px] text-[var(--muted)] mt-2">Mai considerati sospetti: righe da estratto conto, spese fisse, chiusure mensili, immobili importati da file e dati di utenti attivi o archiviati (salvo nomi chiaramente di prova). Le notizie eliminate si trovano in Notizie → Cestino, le voci di Finanza in Finanza → Cestino.</p>
      <Cestino list={cest} onRestore={ripristina} onDone={load} />
    </div>
  );
}
